import React, { useState, useEffect } from 'react';
import { reportAPI } from '../lib/api';
import { ChevronLeft, ChevronRight, DollarSign, FolderOpen, Clock, Users, Download } from 'lucide-react';

export default function ProjectBilling() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => { fetchData(); }, [month]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await reportAPI.getProjectBilling(month);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const changeMonth = (dir) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const monthLabel = (() => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  })();

  const projects = data?.projects || [];
  const totalHours = projects.reduce((s, p) => s + p.total_actual_hours, 0);
  const totalTasks = projects.reduce((s, p) => s + p.task_count, 0);
  const maxHours = Math.max(...projects.map(p => p.total_actual_hours), 1);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="project-billing-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-slate-900">Project Billing Report</h1>
            <p className="text-xs text-slate-400 mt-0.5">Monthly hours per project for client billing</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <button onClick={() => changeMonth(-1)} className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" data-testid="pb-prev-month">
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm font-semibold text-slate-700 min-w-[140px] text-center" data-testid="pb-month-label">{monthLabel}</span>
              <button onClick={() => changeMonth(1)} className="h-8 w-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" data-testid="pb-next-month">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Total Billable Hours', value: `${totalHours.toFixed(0)}h`, icon: Clock, color: 'bg-blue-50 text-blue-600' },
            { label: 'Active Projects', value: projects.length, icon: FolderOpen, color: 'bg-emerald-50 text-emerald-600' },
            { label: 'Total Tasks', value: totalTasks, icon: DollarSign, color: 'bg-purple-50 text-purple-600' },
          ].map((card, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`pb-card-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-400">{card.label}</span>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${card.color}`}>
                  <card.icon size={16} />
                </div>
              </div>
              <div className="text-2xl font-bold text-slate-900">{card.value}</div>
            </div>
          ))}
        </div>

        {/* Project cards */}
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : projects.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 flex flex-col items-center justify-center h-40 gap-2">
            <FolderOpen size={32} className="text-slate-200" />
            <p className="text-sm text-slate-400">No project hours for {monthLabel}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {projects.map((p, i) => (
              <div key={p.project_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`pb-project-${i}`}>
                <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
                      <FolderOpen size={18} className="text-[#002FA7]" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-800">{p.project_name}</h3>
                      {p.client_name && <span className="text-xs text-slate-400">Client: {p.client_name}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
                    <div className="text-left sm:text-right">
                      <div className="text-xl font-bold text-slate-900">{p.total_actual_hours}h</div>
                      <span className="text-[10px] text-slate-400">actual</span>
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="text-sm font-medium text-slate-500">{p.total_estimated_hours}h</div>
                      <span className="text-[10px] text-slate-400">estimated</span>
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="text-sm font-medium text-slate-500">{p.task_count}</div>
                      <span className="text-[10px] text-slate-400">tasks</span>
                    </div>
                  </div>
                </div>

                {/* Hours bar */}
                <div className="px-5 pb-2">
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#002FA7] rounded-full transition-all" style={{ width: `${(p.total_actual_hours / maxHours) * 100}%` }} />
                  </div>
                  <div className="flex items-center gap-4 mt-1">
                    <span className="text-[10px] text-blue-600 font-medium">Regular: {p.regular_hours}h</span>
                    {p.support_hours > 0 && <span className="text-[10px] text-purple-600 font-medium">Support/Meetings: {p.support_hours}h</span>}
                  </div>
                </div>

                {/* Resource breakdown */}
                {p.resources.length > 0 && (
                  <div className="px-5 py-3 bg-slate-50/50 border-t border-slate-100">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Resources</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {p.resources.map((r, ri) => (
                        <span key={ri} className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-full px-2.5 py-1 text-xs text-slate-600">
                          <span className="font-medium">{r.name}</span>
                          <span className="text-slate-400">·</span>
                          <span className="font-semibold text-[#002FA7]">{r.hours}h</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
